import { useState, useEffect, useMemo } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import OfferAllocationEditor from "../ui/OfferAllocationEditor";

const blankTool = {
  name: "",
  category: "",
  unitLabel: "purchase",
  status: "active",
  type: "one_off",
  notes: "",
  offerAllocations: [],
  // Seeded for the one-off path (default).
  oneOffDate: new Date().toISOString().slice(0, 10),
  oneOffAmount: 0,
};

// When type === "one_off", the tool stores a single-month entry in
// priceByMonth (key = YYYY-MM derived from the date field) and a units of 1.
// That way ActualsMatrix-style readers can sum it like any other monthly
// price without special casing.
function blankOneOff() {
  const today = new Date().toISOString().slice(0, 10);
  return { ...blankTool, type: "one_off", oneOffDate: today, oneOffAmount: 0, unitLabel: "purchase" };
}

// Extract the existing one-off date + amount from a Tool's priceByMonth.
// Used when editing an existing one-off so the simplified form prefills.
function extractOneOff(tool) {
  const months = Object.keys(tool?.priceByMonth || {});
  if (months.length === 0) {
    const today = new Date().toISOString().slice(0, 10);
    return { date: today, amount: 0 };
  }
  // pick the most recent month entry — typically there's only one
  months.sort();
  const m = months[months.length - 1];
  return {
    date: `${m}-01`,
    amount: Number(tool.priceByMonth[m]) || 0,
  };
}

export default function ToolForm({ open, onClose, onSubmit, initial, offers = [], departments = [], lockedType = null }) {
  const [form, setForm] = useState(() => initial || blankTool);
  const [errors, setErrors] = useState({});

  // Reset form whenever modal opens or initial changes
  useEffect(() => {
    if (open) {
      let seed;
      if (initial) {
        seed = { ...initial };
        if (initial.type === "one_off") {
          const oo = extractOneOff(initial);
          seed.oneOffDate = oo.date;
          seed.oneOffAmount = oo.amount;
        }
      } else if (lockedType === "one_off") {
        seed = blankOneOff();
      } else {
        seed = { ...blankTool };
        if (lockedType) seed.type = lockedType;
      }
      setForm(seed);
      setErrors({});
    }
  }, [open, initial, lockedType]);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isOneOff = form.type === "one_off";
  // "assign" = no upfront amount; user will match expenses later
  // "manual" = enter date + amount now, stored on priceByMonth
  const mode = form.entryMode || "assign";
  const isManual = isOneOff && mode === "manual";

  const helperText = useMemo(() => {
    if (isOneOff && mode === "assign") return "Create the tool now and match expenses to it from the Expenses tab. Amount appears once a matched charge lands.";
    if (isOneOff && mode === "manual") return "Use Manual entry when the purchase didn't come through QuickBooks (cash, off-platform, etc.).";
    if (form.type === "variable") return "After saving, log each top-up or purchase as a transaction.";
    return "After saving, fill in the Units and Price for each month in the matrix view. Cells start empty.";
  }, [form.type, isOneOff, mode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (isManual) {
      if (!form.oneOffDate) errs.oneOffDate = "Required";
      if (!(Number(form.oneOffAmount) > 0)) errs.oneOffAmount = "Must be > 0";
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }

    let unitsByMonth = form.unitsByMonth || {};
    let priceByMonth = form.priceByMonth || {};
    if (isManual) {
      const m = form.oneOffDate.slice(0, 7);
      unitsByMonth = { [m]: 1 };
      priceByMonth = { [m]: Number(form.oneOffAmount) || 0 };
    } else if (isOneOff) {
      // Assign mode: leave matrix empty so matched expenses drive the amount.
      unitsByMonth = {};
      priceByMonth = {};
    }

    onSubmit({
      ...form,
      type: form.type || "recurring",
      unitsByMonth,
      priceByMonth,
      offerAllocations: (form.offerAllocations || [])
        .filter((a) => {
          const scope = a.scope || (a.offerId ? "offer" : null);
          if (scope === "offer") return !!a.offerId;
          if (scope === "department") return !!a.scopeId;
          if (scope === "agency") return true;
          return false;
        })
        .map((a) => ({ ...a, value: Number(a.value) || 0 })),
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit tool" : (isOneOff ? "Log one-off purchase" : "Add tool")}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {lockedType ? (
          <Input label="Name *" value={form.name} onChange={(e) => update("name", e.target.value)} error={errors.name} placeholder={isOneOff ? "e.g. Million Verifier credits" : "e.g. Slack"} />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Input label="Name *" value={form.name} onChange={(e) => update("name", e.target.value)} error={errors.name} placeholder={isOneOff ? "e.g. Million Verifier credits" : "e.g. Slack"} />
            <Select
              label="Type"
              value={form.type}
              onChange={(e) => update("type", e.target.value)}
            >
              <option value="recurring">Recurring (monthly subscription)</option>
              <option value="variable">Variable (top-ups / credits)</option>
              <option value="one_off">One-time purchase</option>
            </Select>
          </div>
        )}

        {isOneOff ? (
          <>
            <div>
              <span className="block text-xs font-medium text-brand-slate mb-1">How to log the amount</span>
              <div className="inline-flex rounded-lg border border-gray-200 dark:border-[var(--border-soft)] bg-white dark:bg-[var(--bg-surface)] p-0.5">
                <button
                  type="button"
                  onClick={() => update("entryMode", "assign")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    mode === "assign"
                      ? "bg-brand-ink text-white"
                      : "text-brand-slate hover:text-brand-ink dark:hover:text-[var(--text-primary)]"
                  }`}
                >
                  Assign expenses
                </button>
                <button
                  type="button"
                  onClick={() => update("entryMode", "manual")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    mode === "manual"
                      ? "bg-brand-ink text-white"
                      : "text-brand-slate hover:text-brand-ink dark:hover:text-[var(--text-primary)]"
                  }`}
                >
                  Manual entry
                </button>
              </div>
            </div>
            {isManual && (
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Date *"
                  type="date"
                  value={form.oneOffDate || ""}
                  onChange={(e) => update("oneOffDate", e.target.value)}
                  error={errors.oneOffDate}
                />
                <Input
                  label="Amount (USD) *"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.oneOffAmount}
                  onChange={(e) => update("oneOffAmount", e.target.value)}
                  error={errors.oneOffAmount}
                />
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Input label="Category" value={form.category} onChange={(e) => update("category", e.target.value)} placeholder="e.g. Workspace" />
            <Input label="Unit label" value={form.unitLabel} onChange={(e) => update("unitLabel", e.target.value)} placeholder="seats / accounts / —" />
          </div>
        )}

        {!isOneOff && (
          <div>
            <Select label="Status" value={form.status} onChange={(e) => update("status", e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </div>
        )}

        <div className="border-t border-gray-100 dark:border-[var(--border-soft)] pt-3">
          <OfferAllocationEditor
            value={form.offerAllocations || []}
            onChange={(next) => update("offerAllocations", next)}
            offers={offers}
            departments={departments}
          />
        </div>

        <label className="block">
          <span className="block text-xs font-medium text-brand-slate mb-1">Notes</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)]"
          />
        </label>
        <p className="text-xs text-brand-slate">{helperText}</p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">{initial ? "Save" : (isOneOff ? "Log purchase" : "Add tool")}</Button>
        </div>
      </form>
    </Modal>
  );
}